import { Fragment, useRef, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  fetchDefaultSlotOrder,
  saveDefaultSlotOrder,
  changeSlotOrderforAvailableAppointments,
} from "./DataServices/SlotOrderDataServices";
import { Reminder } from "./Models";

interface IDefineSlotOrder {
  open: boolean;
  setOpen: (open: boolean) => void;
  availableAppointments: IAvailableAppointment[];
}

interface IAvailableAppointment {
  id: string;
  listingID: string;
  startTime: Date | null;
  endTime: Date | null;
  capacity: number;
  slotOrder: number;
  reminders: Reminder[];
}

interface DefaultSortOrderItem {
  id: string;
  name: string;
  slotOrder: number;
  startTime: Date | null;
  endTime: Date | null;
  slotCapacity: number;
  reminders: Reminder[];
}

export default function DefineSlotOrder({
  open,
  setOpen,
  availableAppointments,
}: IDefineSlotOrder) {
  const cancelButtonRef = useRef(null);
  const [defaultSortOrder, setDefaultSortOrder] = useState<
    DefaultSortOrderItem[]
  >([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (availableAppointments.length == 0) {
          const data = await fetchDefaultSlotOrder();
          setDefaultSortOrder(data);
        } else {
          const transformedDefaultSortOrder = availableAppointments.map(
            (appointment) => {
              // alert(appointment.slotOrder);
              // alert(appointment.startTime);
              const startDateTime = appointment.startTime as Date;
              const endDateTime = appointment.endTime as Date;

              const options: Intl.DateTimeFormatOptions = {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
              };

              return {
                id: appointment.id,
                name: `${new Date(startDateTime).toLocaleString(
                  "en-US",
                  options
                )} To ${new Date(endDateTime).toLocaleString(
                  "en-US",
                  options
                )}`,
                slotOrder: appointment.slotOrder,
                startTime: new Date(startDateTime),
                endTime: new Date(endDateTime),
                slotCapacity: appointment.capacity,
                reminders: appointment.reminders
              };
            }
          );

          const sortedDefaultSortOrder: DefaultSortOrderItem[] = transformedDefaultSortOrder.sort(
            (a, b) => a.slotOrder - b.slotOrder
          );
          setDefaultSortOrder(sortedDefaultSortOrder);
        }
      } catch (error) {
        console.log(error);
      }
    };

    if (open) {
      // Check if the component is open, then fetch the data
      fetchData();
    }
  }, [open]);

  useEffect(() => {

  }, [defaultSortOrder]);

  const handleDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(defaultSortOrder);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setDefaultSortOrder(items);
  };

  const SavelSlotOrder = async () => {
    const success =
      availableAppointments.length === 0
        ? await saveDefaultSlotOrder(defaultSortOrder)
        : await changeSlotOrderforAvailableAppointments(defaultSortOrder);

    if (success) {
      setOpen(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl sm:p-4">
                <div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      Define Slot Order TMAK
                    </Dialog.Title>
                    <div className="overflow-hidden rounded-md border border-gray-300 bg-white">
                      <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="defaultSortOrder">
                          {(provided) => (
                            <ul
                              role="list"
                              className="divide-y divide-gray-300"
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                            >
                              {defaultSortOrder.map((item, index) => (
                                <Draggable
                                  key={item.id}
                                  draggableId={item.id.toString()}
                                  index={index}
                                >
                                  {(provided) => (
                                    <li
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className="px-6 py-4"
                                    >
                                      {item.name}
                                    </li>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </ul>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                    onClick={SavelSlotOrder}
                  >
                    Save Slot Order
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                    onClick={() => setOpen(false)}
                    ref={cancelButtonRef}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
